import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, CheckCircle, AlertCircle, Shield, Key } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [passwords, setPasswords] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [status, setStatus] = useState('validating'); // 'validating', 'valid', 'invalid', 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [tokenData, setTokenData] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: []
  });

  // Password strength checker
  const checkPasswordStrength = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };

    const score = Object.values(checks).filter(Boolean).length;
    const feedback = [];

    if (!checks.length) feedback.push('At least 8 characters');
    if (!checks.uppercase) feedback.push('One uppercase letter');
    if (!checks.lowercase) feedback.push('One lowercase letter');
    if (!checks.number) feedback.push('One number');
    if (!checks.special) feedback.push('One special character');

    return { score, feedback, checks };
  };

  // Get API URL
  const getApiUrl = () => {
    const isLocalhost = window.location.hostname === 'localhost';
    const isPlatformDomain = window.location.hostname === 'platform.glowytics.com';
    
    if (isLocalhost) {
      return 'http://localhost:5000';
    } else if (isPlatformDomain) {
      return 'https://api.glowytics.com';
    } else {
      return process.env.NODE_ENV === 'production' 
        ? 'https://api.glowytics.com' 
        : 'http://localhost:5000';
    }
  };

  // Validate token on component mount
  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setMessage('No reset token provided. Please check your email link.');
      return;
    }

    const validateToken = async () => {
      try {
        const apiUrl = getApiUrl();
        const response = await fetch(`${apiUrl}/api/password-reset/validate-token/${token}`);
        const data = await response.json();

        if (response.ok && data.valid) {
          setStatus('valid');
          setTokenData(data);
          setMessage('');
        } else {
          setStatus('invalid');
          setMessage(data.error || 'Invalid or expired reset token.');
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setStatus('invalid');
        setMessage('Unable to validate reset token. Please try again.');
      }
    };

    validateToken();
  }, [token]);

  // Update password strength when password changes
  useEffect(() => {
    if (passwords.password) {
      setPasswordStrength(checkPasswordStrength(passwords.password));
    } else {
      setPasswordStrength({ score: 0, feedback: [] });
    }
  }, [passwords.password]);

  const handlePasswordChange = (field, value) => {
    setPasswords(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');

    // Validation
    if (passwords.password !== passwords.confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match.');
      return;
    }

    if (passwordStrength.score < 4) {
      setStatus('error');
      setMessage('Password does not meet security requirements.');
      return;
    }

    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/api/password-reset/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          newPassword: passwords.password,
          confirmPassword: passwords.confirmPassword
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage('Password reset successfully! You can now login with your new password.');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setStatus('error');
        setMessage(data.error || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setStatus('error');
      setMessage('Unable to reset password. Please check your connection and try again.');
    }
  };

  const getPasswordStrengthColor = (score) => {
    if (score < 2) return 'bg-red-500';
    if (score < 4) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = (score) => {
    if (score < 2) return 'Weak';
    if (score < 4) return 'Medium';
    return 'Strong';
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left Column: Form */}
      <div className="w-full lg:w-[45%] xl:w-[40%] flex flex-col justify-center items-center p-8 sm:p-12 bg-white shadow-xl z-10">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <img
            src="/Calisma-Yuzeyi-20@5x-4.png"
            alt="Glowytics Logo"
            className="h-9 mb-10"
          />

          {/* Back to Login Link */}
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-500 mb-8 transition"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Login
          </Link>

          {/* Header */}
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            {status === 'success' ? 'Password Reset Complete!' : 'Set New Password'}
          </h1>
          <p className="text-sm text-gray-500 mb-8">
            {status === 'success' 
              ? 'Your password has been successfully updated.'
              : 'Choose a strong password to secure your account.'
            }
          </p>

          {/* Token Validation Status */}
          {status === 'validating' && (
            <div className="mb-6 rounded-md bg-blue-50 p-4 border border-blue-200">
              <div className="flex">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-3 mt-0.5"></div>
                <div>
                  <h3 className="text-sm font-medium text-blue-800">Validating Reset Token</h3>
                  <p className="text-xs text-blue-700 mt-1">Please wait while we verify your reset link...</p>
                </div>
              </div>
            </div>
          )}

          {/* Token Invalid */}
          {status === 'invalid' && (
            <div className="mb-6 rounded-md bg-red-50 p-4 border border-red-200">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Invalid Reset Link</h3>
                  <p className="text-xs text-red-700 mt-1">{message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {status === 'success' && (
            <div className="mb-6 rounded-md bg-green-50 p-4 border border-green-200">
              <div className="flex">
                <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">Success!</h3>
                  <p className="text-xs text-green-700 mt-1">{message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {status === 'error' && (
            <div className="mb-6 rounded-md bg-red-50 p-4 border border-red-200">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="text-xs text-red-700 mt-1">{message}</p>
                </div>
              </div>
            </div>
          )}

          {/* User Info (if token is valid) */}
          {status === 'valid' && tokenData && (
            <div className="mb-6 rounded-md bg-blue-50 p-4 border border-blue-200">
              <div className="flex">
                <Shield className="h-5 w-5 text-blue-500 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-blue-800">Reset Password for</h3>
                  <p className="text-xs text-blue-700 mt-1">
                    {tokenData.user.name} ({tokenData.user.email})
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Reset Form */}
          {status === 'valid' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* New Password */}
              <div>
                <label htmlFor="password" className="block text-xs font-medium text-gray-600 mb-1">
                  New Password
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={passwords.password}
                    onChange={(e) => handlePasswordChange('password', e.target.value)}
                    className="block w-full pl-9 pr-10 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition sm:text-sm"
                    placeholder="Enter new password"
                    disabled={status === 'loading'}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {passwords.password && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-600">Password Strength</span>
                      <span className={`text-xs font-medium ${
                        passwordStrength.score < 2 ? 'text-red-600' :
                        passwordStrength.score < 4 ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {getPasswordStrengthText(passwordStrength.score)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all duration-300 ${getPasswordStrengthColor(passwordStrength.score)}`}
                        style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                      ></div>
                    </div>
                    {passwordStrength.feedback.length > 0 && (
                      <div className="mt-1">
                        <p className="text-xs text-gray-600">Requirements:</p>
                        <ul className="text-xs text-gray-500 list-disc list-inside">
                          {passwordStrength.feedback.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-xs font-medium text-gray-600 mb-1">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={passwords.confirmPassword}
                    onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    className="block w-full pl-9 pr-10 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition sm:text-sm"
                    placeholder="Confirm new password"
                    disabled={status === 'loading'}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>

                {/* Password Match Indicator */}
                {passwords.confirmPassword && (
                  <div className="mt-1">
                    {passwords.password === passwords.confirmPassword ? (
                      <p className="text-xs text-green-600 flex items-center">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Passwords match
                      </p>
                    ) : (
                      <p className="text-xs text-red-600 flex items-center">
                        <AlertCircle className="h-3 w-3 mr-1" />
                        Passwords do not match
                      </p>
                    )}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={status === 'loading' || passwordStrength.score < 4 || passwords.password !== passwords.confirmPassword}
                className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'loading' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Updating Password...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4" />
                    Update Password
                  </>
                )}
              </button>
            </form>
          )}

          {/* Success Actions */}
          {status === 'success' && (
            <div className="space-y-4">
              <Link
                to="/login"
                className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition"
              >
                <ArrowLeft className="h-4 w-4" />
                Go to Login
              </Link>
            </div>
          )}

          {/* Invalid Token Actions */}
          {status === 'invalid' && (
            <div className="space-y-4">
              <Link
                to="/forgot-password"
                className="w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition"
              >
                Request New Reset Link
              </Link>
              <Link
                to="/login"
                className="w-full text-center text-sm text-indigo-600 hover:text-indigo-500 transition"
              >
                Back to Login
              </Link>
            </div>
          )}

          {/* Security Notice */}
          <div className="mt-8 p-3 bg-gray-50 rounded-md">
            <p className="text-xs text-gray-600 leading-relaxed">
              <strong>Security Tip:</strong> Use a unique password that you don't use for other accounts. 
              Consider using a password manager to generate and store secure passwords.
            </p>
          </div>
        </div>
      </div>

      {/* Right Column: Visual */}
      <div className="hidden lg:flex lg:w-[55%] xl:w-[60%] bg-gradient-to-br from-[#0D122B] via-[#101C48] to-[#1F2A68] relative items-center justify-center p-10 xl:p-16 text-white overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0 opacity-40 mix-blend-soft-light">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="absolute w-[700px] h-[700px] bg-gradient-radial from-blue-600/30 via-transparent to-transparent rounded-full -top-60 -left-60 animate-pulse-slow filter blur-3xl"></div>
          <div className="absolute w-[600px] h-[600px] bg-gradient-radial from-violet-700/20 via-transparent to-transparent rounded-full -bottom-60 -right-60 animate-pulse-slow animation-delay-2000 filter blur-3xl"></div>
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 flex flex-col items-center text-center max-w-xl">
          {/* Shield Icon for Security */}
          <div className="w-28 h-28 mb-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
            <Shield className="w-12 h-12 text-blue-300/80" strokeWidth={1} />
          </div>

          <h2 className="text-3xl xl:text-4xl font-bold mb-4 leading-tight">
            Secure Password Reset
          </h2>
          <p className="text-base xl:text-lg text-indigo-100/70 mb-10">
            Create a strong password to protect your marketing data and campaigns from unauthorized access.
          </p>

          {/* Security Features */}
          <div className="flex justify-center space-x-8 text-indigo-200/70">
            <div className="flex flex-col items-center" title="Encrypted Storage">
              <Shield className="h-6 w-6 mb-1" strokeWidth={1.5}/>
              <span className="text-xs font-light">Encrypted</span>
            </div>
            <div className="flex flex-col items-center" title="Secure Process">
              <Key className="h-6 w-6 mb-1" strokeWidth={1.5}/>
              <span className="text-xs font-light">Secure</span>
            </div>
            <div className="flex flex-col items-center" title="Protected Access">
              <CheckCircle className="h-6 w-6 mb-1" strokeWidth={1.5}/>
              <span className="text-xs font-light">Protected</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
